import {
  BufferGeometry,
  DoubleSide,
  ExtrudeGeometry,
  Float32BufferAttribute,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshStandardMaterial,
  Path,
  Shape,
  ShapeGeometry,
  ShapeUtils,
  Vector2,
} from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";

import type { PolygonWithHoles } from "../../world/geometry.ts";
import type {
  BuildingFootprintData,
  DevelopmentWorldTile,
  TerrainTileData,
} from "../../world/schema/tile.ts";
import type { DevelopmentTileSet } from "../../world/schema/tileSet.ts";

export interface DevelopmentTileSetViewOptions {
  readonly showTileDiagnostics?: boolean;
}

export interface DevelopmentTileSetRenderDiagnostics {
  readonly tileCount: number;
  readonly terrainMeshCount: number;
  readonly sourceBuildingBatchCount: number;
  readonly diagnosticFootprintBatchCount: number;
  readonly sourceHeightBuildingCount: number;
  readonly diagnosticFootprintCount: number;
  readonly courtyardCount: number;
}

export interface DevelopmentTileSetView {
  readonly object: Group;
  readonly diagnostics: DevelopmentTileSetRenderDiagnostics;
  dispose(): void;
}

const terrainMaterial = new MeshStandardMaterial({
  color: 0x737866,
  roughness: 0.98,
  metalness: 0,
});

const buildingMaterial = new MeshStandardMaterial({
  color: 0xa79d8d,
  roughness: 0.95,
  metalness: 0,
});

const diagnosticFootprintMaterial = new MeshStandardMaterial({
  color: 0x816b58,
  roughness: 1,
  metalness: 0,
  side: DoubleSide,
  transparent: true,
  opacity: 0.76,
  depthWrite: false,
  polygonOffset: true,
  polygonOffsetFactor: -2,
  polygonOffsetUnits: -2,
});

const tileDiagnosticMaterial = new LineBasicMaterial({
  color: 0xb77958,
  transparent: true,
  opacity: 0.82,
  depthTest: false,
});

function assertTerrainShape(terrain: Readonly<TerrainTileData>): void {
  if (!Number.isSafeInteger(terrain.samplesPerSide) ||
      terrain.samplesPerSide < 2) {
    throw new Error("Terrain samplesPerSide must be an integer of at least 2");
  }
  if (!Number.isFinite(terrain.spacingM) || terrain.spacingM <= 0) {
    throw new Error("Terrain spacingM must be finite and positive");
  }
  const expectedSampleCount = terrain.samplesPerSide ** 2;
  if (terrain.heightsM.length !== expectedSampleCount) {
    throw new Error(
      `Terrain expected ${expectedSampleCount} heights, received ${terrain.heightsM.length}`,
    );
  }
  if (terrain.heightsM.some((height) => !Number.isFinite(height))) {
    throw new Error("Terrain heights must all be finite");
  }
}

/**
 * Builds one tile-local terrain mesh. The caller positions it at `tile.origin`
 * so adjacent tiles retain smaller vertex coordinates and exact shared seams.
 */
export function createTerrainGeometry(
  terrain: Readonly<TerrainTileData>,
): BufferGeometry {
  assertTerrainShape(terrain);

  const side = terrain.samplesPerSide;
  const positions = new Float32Array(side * side * 3);
  let positionOffset = 0;

  for (let z = 0; z < side; z += 1) {
    for (let x = 0; x < side; x += 1) {
      const height = terrain.heightsM[z * side + x];
      if (height === undefined) {
        throw new Error(`Terrain height (${x}, ${z}) is missing`);
      }
      positions[positionOffset] = x * terrain.spacingM;
      positions[positionOffset + 1] = height;
      positions[positionOffset + 2] = z * terrain.spacingM;
      positionOffset += 3;
    }
  }

  const indices: number[] = [];
  for (let z = 0; z < side - 1; z += 1) {
    for (let x = 0; x < side - 1; x += 1) {
      const northWest = z * side + x;
      const northEast = northWest + 1;
      const southWest = northWest + side;
      const southEast = southWest + 1;
      indices.push(
        northWest,
        southWest,
        northEast,
        northEast,
        southWest,
        southEast,
      );
    }
  }

  const geometry = new BufferGeometry();
  geometry.name = "source-terrain";
  geometry.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function ringToShapePoints(
  ring: Readonly<PolygonWithHoles["outer"]>,
): Vector2[] {
  const lastIndex = ring.length - 1;
  const isClosed = lastIndex >= 1 &&
    ring[0]?.x === ring[lastIndex]?.x &&
    ring[0]?.z === ring[lastIndex]?.z;
  const source = isClosed ? ring.slice(0, lastIndex) : ring;
  return source.map((point) => new Vector2(point.x, -point.z));
}

export function createPolygonShape(
  footprint: Readonly<PolygonWithHoles>,
): Shape {
  const outer = ringToShapePoints(footprint.outer);
  if (outer.length < 3) {
    throw new Error("Building outer ring must contain at least three points");
  }
  if (!ShapeUtils.isClockWise(outer)) {
    outer.reverse();
  }

  const shape = new Shape(outer);
  shape.autoClose = true;
  for (const holeRing of footprint.holes) {
    const hole = ringToShapePoints(holeRing);
    if (hole.length < 3) {
      throw new Error("Building hole ring must contain at least three points");
    }
    if (ShapeUtils.isClockWise(hole)) {
      hole.reverse();
    }
    const path = new Path(hole);
    path.autoClose = true;
    shape.holes.push(path);
  }
  return shape;
}

/**
 * Returns source-derived tile-local building geometry, or `null` when the DTO
 * has no source height. M1 must not manufacture a fallback building height.
 */
function createExtrudedFootprintGeometry(
  building: Readonly<BuildingFootprintData>,
  heightM: number,
  geometryName: string,
): BufferGeometry {
  if (!Number.isFinite(heightM) || heightM <= 0) {
    throw new Error(`Building ${building.id} extrusion height must be positive`);
  }
  if (!Number.isFinite(building.baseElevationM)) {
    throw new Error(`Building ${building.id} baseElevationM must be finite`);
  }

  const geometry = new ExtrudeGeometry(
    createPolygonShape(building.footprint),
    {
      depth: heightM,
      steps: 1,
      bevelEnabled: false,
      curveSegments: 1,
    },
  );
  // Shape Y is -local Z. Rotate the extrusion axis onto +Y and restore +Z.
  geometry.rotateX(-Math.PI / 2);
  geometry.translate(0, building.baseElevationM, 0);
  geometry.deleteAttribute("uv");
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  geometry.name = geometryName;
  return geometry;
}

export function createBuildingFootprintGeometry(
  building: Readonly<BuildingFootprintData>,
): BufferGeometry | null {
  if (building.heightM === undefined) {
    return null;
  }
  if (!Number.isFinite(building.heightM) || building.heightM <= 0) {
    throw new Error(`Building ${building.id} heightM must be positive`);
  }
  return createExtrudedFootprintGeometry(
    building,
    building.heightM,
    `source-building:${building.id}`,
  );
}

export function createDiagnosticFootprintGeometry(
  building: Readonly<BuildingFootprintData>,
): BufferGeometry {
  if (!Number.isFinite(building.baseElevationM)) {
    throw new Error(`Building ${building.id} baseElevationM must be finite`);
  }
  const geometry = new ShapeGeometry(
    createPolygonShape(building.footprint),
    1,
  );
  // Shape Y is -local Z. Keep the polygon exactly at source-derived base Y.
  geometry.rotateX(-Math.PI / 2);
  geometry.translate(0, building.baseElevationM, 0);
  geometry.deleteAttribute("uv");
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  geometry.name = `diagnostic-footprint:${building.id}`;
  return geometry;
}

function createTileTerrainMesh(tile: Readonly<DevelopmentWorldTile>): Mesh {
  const mesh = new Mesh(createTerrainGeometry(tile.terrain), terrainMaterial);
  mesh.name = `terrain:${tile.id}`;
  mesh.position.set(tile.origin.x, 0, tile.origin.z);
  mesh.receiveShadow = true;
  mesh.userData = {
    tileId: tile.id,
    sourceIds: [...tile.sourceIds],
  };
  return mesh;
}

interface TileBuildingBatch {
  readonly sourceMesh: Mesh | null;
  readonly diagnosticMesh: Mesh | null;
  readonly sourceHeightBuildingCount: number;
  readonly diagnosticFootprintCount: number;
  readonly courtyardCount: number;
}

function mergeGeometryBatch(
  geometries: readonly BufferGeometry[],
  batchName: string,
): BufferGeometry | null {
  if (geometries.length === 0) {
    return null;
  }
  const merged = geometries.length === 1
    ? geometries[0]
    : mergeGeometries([...geometries]);
  if (merged === null || merged === undefined) {
    for (const geometry of geometries) {
      geometry.dispose();
    }
    throw new Error(`Could not merge ${batchName}`);
  }
  if (geometries.length > 1) {
    for (const geometry of geometries) {
      geometry.dispose();
    }
  }
  merged.name = batchName;
  return merged;
}

function createTileBuildingBatch(
  tile: Readonly<DevelopmentWorldTile>,
): TileBuildingBatch {
  const sourceGeometries: BufferGeometry[] = [];
  const diagnosticGeometries: BufferGeometry[] = [];
  const sourceBuildingIds: string[] = [];
  const diagnosticFootprintIds: string[] = [];
  let courtyardCount = 0;

  for (const building of tile.buildings) {
    const geometry = createBuildingFootprintGeometry(building);
    if (geometry === null) {
      diagnosticGeometries.push(createDiagnosticFootprintGeometry(building));
      diagnosticFootprintIds.push(building.id);
    } else {
      sourceGeometries.push(geometry);
      sourceBuildingIds.push(building.id);
    }
    courtyardCount += building.footprint.holes.length;
  }

  const sourceGeometry = mergeGeometryBatch(
    sourceGeometries,
    `source-building-batch:${tile.id}`,
  );
  const diagnosticGeometry = mergeGeometryBatch(
    diagnosticGeometries,
    `diagnostic-footprint-batch:${tile.id}`,
  );
  const sourceMesh = sourceGeometry === null
    ? null
    : new Mesh(sourceGeometry, buildingMaterial);
  if (sourceMesh !== null) {
    sourceMesh.name = `buildings:${tile.id}`;
    sourceMesh.position.set(tile.origin.x, 0, tile.origin.z);
    sourceMesh.castShadow = true;
    sourceMesh.receiveShadow = true;
    sourceMesh.userData = {
      tileId: tile.id,
      buildingIds: sourceBuildingIds,
      heightEvidence: "source",
    };
  }
  const diagnosticMesh = diagnosticGeometry === null
    ? null
    : new Mesh(diagnosticGeometry, diagnosticFootprintMaterial);
  if (diagnosticMesh !== null) {
    diagnosticMesh.name = `diagnostic-footprints:${tile.id}`;
    diagnosticMesh.position.set(tile.origin.x, 0, tile.origin.z);
    diagnosticMesh.receiveShadow = true;
    diagnosticMesh.userData = {
      tileId: tile.id,
      buildingIds: diagnosticFootprintIds,
      heightEvidence: "missing-flat-footprint-diagnostic",
    };
  }

  return {
    sourceMesh,
    diagnosticMesh,
    sourceHeightBuildingCount: sourceGeometries.length,
    diagnosticFootprintCount: diagnosticGeometries.length,
    courtyardCount,
  };
}

function terrainCornerHeight(
  terrain: Readonly<TerrainTileData>,
  x: 0 | 1,
  z: 0 | 1,
): number {
  const side = terrain.samplesPerSide;
  const column = x === 0 ? 0 : side - 1;
  const row = z === 0 ? 0 : side - 1;
  const value = terrain.heightsM[row * side + column];
  if (value === undefined) {
    throw new Error("Terrain corner height is missing");
  }
  return value;
}

function createTileDiagnosticLines(
  tile: Readonly<DevelopmentWorldTile>,
): LineSegments {
  const maximum = tile.terrain.spacingM *
    (tile.terrain.samplesPerSide - 1);
  const lift = 0.12;
  const northWest = [0, terrainCornerHeight(tile.terrain, 0, 0) + lift, 0];
  const northEast = [
    maximum,
    terrainCornerHeight(tile.terrain, 1, 0) + lift,
    0,
  ];
  const southEast = [
    maximum,
    terrainCornerHeight(tile.terrain, 1, 1) + lift,
    maximum,
  ];
  const southWest = [
    0,
    terrainCornerHeight(tile.terrain, 0, 1) + lift,
    maximum,
  ];
  const positions = new Float32Array([
    ...northWest, ...northEast,
    ...northEast, ...southEast,
    ...southEast, ...southWest,
    ...southWest, ...northWest,
  ]);
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new Float32BufferAttribute(positions, 3));
  const lines = new LineSegments(geometry, tileDiagnosticMaterial);
  lines.name = `tile-diagnostic:${tile.id}`;
  lines.position.set(tile.origin.x, 0, tile.origin.z);
  lines.renderOrder = 10;
  return lines;
}

export function createDevelopmentTileSetView(
  tileSet: Readonly<DevelopmentTileSet>,
  options: Readonly<DevelopmentTileSetViewOptions> = {},
): DevelopmentTileSetView {
  const object = new Group();
  object.name = `development-tile-set:${tileSet.aoiId}`;
  object.userData = {
    aoiId: tileSet.aoiId,
    sourceIds: [...tileSet.sourceIds],
    schemaVersion: tileSet.schemaVersion,
  };

  let terrainMeshCount = 0;
  let sourceBuildingBatchCount = 0;
  let diagnosticFootprintBatchCount = 0;
  let sourceHeightBuildingCount = 0;
  let diagnosticFootprintCount = 0;
  let courtyardCount = 0;

  for (const tile of tileSet.tiles) {
    object.add(createTileTerrainMesh(tile));
    terrainMeshCount += 1;

    const buildingBatch = createTileBuildingBatch(tile);
    if (buildingBatch.sourceMesh !== null) {
      object.add(buildingBatch.sourceMesh);
      sourceBuildingBatchCount += 1;
    }
    if (buildingBatch.diagnosticMesh !== null) {
      object.add(buildingBatch.diagnosticMesh);
      diagnosticFootprintBatchCount += 1;
    }
    sourceHeightBuildingCount += buildingBatch.sourceHeightBuildingCount;
    diagnosticFootprintCount += buildingBatch.diagnosticFootprintCount;
    courtyardCount += buildingBatch.courtyardCount;

    if (options.showTileDiagnostics === true) {
      object.add(createTileDiagnosticLines(tile));
    }
  }

  const diagnostics: DevelopmentTileSetRenderDiagnostics = {
    tileCount: tileSet.tiles.length,
    terrainMeshCount,
    sourceBuildingBatchCount,
    diagnosticFootprintBatchCount,
    sourceHeightBuildingCount,
    diagnosticFootprintCount,
    courtyardCount,
  };

  let disposed = false;
  return {
    object,
    diagnostics,
    dispose(): void {
      if (disposed) {
        return;
      }
      disposed = true;
      object.traverse((child) => {
        if (child instanceof Mesh || child instanceof LineSegments) {
          child.geometry.dispose();
        }
      });
      object.removeFromParent();
      object.clear();
    },
  };
}
